import React, { useState, useEffect, useCallback } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Layout from "../../components/global/layout";
import swal from "sweetalert";

const formatCurrency = (val) => {
  const num = Number(val ?? 0);
  const truncated = Math.trunc(num * 100) / 100;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(truncated);
};

const formatDate = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatBillAddr = (addr) => {
  if (!addr) return "—";
  return (
    [addr.Line1, addr.Line2, addr.Line3, addr.Line4, addr.City, addr.Country]
      .filter(Boolean)
      .join(", ") || "—"
  );
};

const formatShipAddr = (addr) => {
  if (!addr) return "—";
  return (
    [
      addr.Line1,
      addr.Line2,
      addr.Line3,
      addr.Line4,
      addr.City,
      addr.CountrySubDivisionCode,
      addr.PostalCode,
      addr.Country,
    ]
      .filter(Boolean)
      .join(", ") || "—"
  );
};

const normalize = (est) => {
  if (!est) return {};
  const raw = est.raw || {};
  const isQB = est.type === "quickbookEstimate";

  const customerName =
    est.customerName ||
    est.customer_name ||
    raw.CustomerRef?.name ||
    est.displayName ||
    "—";

  const email = est.email || raw.BillEmail?.Address || est.displayEmail || "—";

  let docNumber =
    est.docNumber || est.doc_number || est.estimate_no || raw.DocNumber || "—";
  const customFields = raw.CustomField || est.CustomField || [];
  const topsField = customFields.find(
    (f) => f.Name === "Tops Shipping Systems, Corp",
  );
  if (
    topsField &&
    topsField.StringValue &&
    topsField.StringValue !== "ORD - null"
  ) {
    docNumber = topsField.StringValue.replace("ORD - ", "");
  }

  let rawStatus =
    est.status || raw.TxnStatus || est.mainRequest?.status || "PENDING";
  if (
    rawStatus === "ESTIMATE" ||
    rawStatus === "ESTIMATED" ||
    rawStatus === "estimated"
  ) {
    rawStatus = "Pending";
  } else if (rawStatus === "REJECT") {
    rawStatus = "REJECTED";
  } else if (rawStatus === "Converted") {
    rawStatus = "Closed";
  }
  const status = rawStatus;

  let rawLines = [];
  if (isQB) {
    rawLines =
      est.originalLineItems ||
      raw.Line?.filter((l) => l.DetailType === "SalesItemLineDetail") ||
      [];
  } else {
    rawLines = est.line_items || est.product_line_items || [];
  }

  const lines = rawLines.map((l) => ({
    id: l.Id || l._id || Math.random().toString(),
    name:
      l.Description ||
      l.description ||
      l.SalesItemLineDetail?.ItemRef?.name ||
      l.item_name ||
      "—",
    qty: Number(
      l.quantity || l.SalesItemLineDetail?.Qty || l.Qty || l.qty || 0,
    ),
    unitPrice: Number(
      l.rate ||
        l.unit_price ||
        l.SalesItemLineDetail?.UnitPrice ||
        l.UnitPrice ||
        l.unitPrice ||
        0,
    ),
    amount: Number(l.Amount || l.amount || 0),
  }));

  const subTotal = lines.reduce((acc, line) => acc + line.amount, 0);

  let discountValue = 0;
  let isDiscountPercent = false;
  let discountAmount = 0;

  if (isQB) {
    const discountLine = raw.Line?.find(
      (l) => l.DetailType === "DiscountLineDetail",
    );
    if (discountLine) {
      discountAmount = Number(discountLine.Amount || 0);
      isDiscountPercent =
        discountLine.DiscountLineDetail?.PercentBased || false;
      discountValue = Number(
        discountLine.DiscountLineDetail?.DiscountPercent || 0,
      );
    }
  } else {
    discountValue = Number(est.discount_value || 0);
    isDiscountPercent = est.discount_type === "percent";
    discountAmount = isDiscountPercent
      ? ((subTotal + est?.markup_amount) * discountValue) / 100
      : discountValue;
  }

  const taxLines =
    isQB && raw.TxnTaxDetail?.TaxLine
      ? raw.TxnTaxDetail.TaxLine.map((tl) => ({
          amount: Number(tl.Amount || 0),
          percent: Number(tl.TaxLineDetail?.TaxPercent || 0),
          netAmount: Number(tl.TaxLineDetail?.NetAmountTaxable || 0),
        }))
      : [];

  const totalTax = Number(
    raw.TxnTaxDetail?.TotalTax ?? est.totalTax ?? est.total_tax ?? 0,
  );
  const total = subTotal - discountAmount;
  const grandTotal = total + totalTax;

  let billAddr = null;
  if (isQB && raw.BillAddr) {
    billAddr = {
      Line1: raw.BillAddr.Line1,
      Line2: raw.BillAddr.Line2,
      Line3: raw.BillAddr.Line3,
      Line4: raw.BillAddr.Line4,
      City: raw.BillAddr.City,
      CountrySubDivisionCode: raw.BillAddr.CountrySubDivisionCode,
      PostalCode: raw.BillAddr.PostalCode,
      Country: raw.BillAddr.Country,
    };
  } else if (est.billingAddress) {
    billAddr = {
      Line1: est.billingAddress.bill_line1,
      Line2: est.billingAddress.bill_line2,
      City: est.billingAddress.bill_city,
      Country: est.billingAddress.bill_country,
      PostalCode: est.billingAddress.bill_postal_code,
    };
  }

  let shipAddr = null;
  if (isQB && raw.ShipAddr) {
    shipAddr = {
      Line1: raw.ShipAddr.Line1,
      Line2: raw.ShipAddr.Line2,
      Line3: raw.ShipAddr.Line3,
      Line4: raw.ShipAddr.Line4,
      City: raw.ShipAddr.City,
      CountrySubDivisionCode: raw.ShipAddr.CountrySubDivisionCode,
      PostalCode: raw.ShipAddr.PostalCode,
      Country: raw.ShipAddr.Country,
    };
  } else if (est.shippingAddress) {
    shipAddr = {
      Line1: est.shippingAddress.ship_line1,
      Line2: est.shippingAddress.ship_line2,
      City: est.shippingAddress.ship_city,
      Country: est.shippingAddress.ship_country,
      PostalCode: est.shippingAddress.ship_postal_code,
    };
  }

  return {
    _id: est._id,
    qbId: est.qbId || raw.Id || null,
    docNumber,
    customerName,
    email,
    status,
    txnDate: est.txnDate || est.txn_date || raw.TxnDate || null,
    expirationDate:
      est.expirationDate || est.expiration_date || raw.ExpirationDate || null,
    createdAt: est.createdAt || raw.MetaData?.CreateTime || null,
    updatedAt: est.updatedAt || raw.MetaData?.LastUpdatedTime || null,

    new_amount: est?.totalAmount || est?.total_amount || 0,
    subTotal,
    discountAmount,
    discountValue,
    isDiscountPercent,
    totalTax,
    totalAmount: total,
    grandTotal,
    total_amount: est?.total_amount || grandTotal,
    markup_type: est.markup_type || est?.markup?.type || null,
    markup_amount: est.markup_amount || est?.markup?.amount || 0,
    markup_value: est.markup_value || est?.markup?.value || 0,

    BillAddr: billAddr,
    ShipAddr: shipAddr,
    Line: lines,
    taxLines,
    currencyCode: raw.CurrencyRef?.value || "USD",
    CustomerMemo: est.customer_memo || raw.CustomerMemo?.value || null,
    privateNote: est.private_note || raw.PrivateNote || null,
    negotiateMessage: est.negotiateMessage || raw.negotiateMessage || null,
    shipMethod: est.ship_method || raw.ShipMethodRef?.name || null,
    addedBy: est.addedBy || null,
    estimateType: est.estimateType || raw.estimateType || est.mainRequest?.estimateType || "full",
    partialMode: est.partialMode || raw.partialMode || est.mainRequest?.partialMode || "percentage",
    partialValue: est.partialValue !== undefined ? est.partialValue : (raw.partialValue ?? est.mainRequest?.partialValue ?? 0),

    _isQB: isQB || !!raw.Id,
    _isLocal: est.type === "generateQuote" || !!est._id,
    _type: est.type || "unknown",
  };
};

const statusColor = (status = "") => {
  const map = {
    Accepted: {
      bg: "bg-[#F0FEF4]",
      text: "text-[#008236]",
      border: "border-[#0a7822a6]",
      dot: "bg-[#0a7822a6]",
    },
    Pending: {
      bg: "bg-amber-50",
      text: "text-amber-700",
      border: "border-amber-200",
      dot: "bg-amber-500",
    },
    Closed: {
      bg: "bg-gray-100",
      text: "text-gray-500",
      border: "border-gray-200",
      dot: "bg-gray-400",
    },
    Rejected: {
      bg: "bg-red-50",
      text: "text-red-600",
      border: "border-red-200",
      dot: "bg-red-500",
    },
    negotiate: {
      bg: "bg-purple-50",
      text: "text-purple-600",
      border: "border-purple-200",
      dot: "bg-purple-500",
    },
  };
  return (
    map[status] || {
      bg: "bg-gray-100",
      text: "text-gray-500",
      border: "border-gray-200",
      dot: "bg-gray-400",
    }
  );
};

const StatusBadge = ({ status }) => {
  const c = statusColor(status || "");
  const label = status ? status : "—";
  return (
    <span
      className={`inline-flex capitalize items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${c.bg} ${c.text} ${c.border}`}
    >
      <span className={`size-1.5 rounded-full ${c.dot}`} />
      {label}
    </span>
  );
};

export default function QuickBookEstimateDetail() {
  const { id } = useParams();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const triggerDate = searchParams.get("date");
  const history = useNavigate();

  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  const canConvert = (est) => est?.status === "Accepted" && est?._isQB;

  const fetchEstimate = useCallback(
    async (estimateId) => {
      setPageLoading(true);
      loader(true);
      try {
        const res = await ApiClient.get(
          `quickbooks-docs/estimates/list?page=1&limit=50&id=${estimateId}`,
        ).catch(() => null);

        if (res?.success) {
          const list = (Array.isArray(res.data) ? res.data : []).map(normalize);
          const match = list.find(
            (e) =>
              e._id === estimateId ||
              e.qbId?.toString() === estimateId ||
              e?.originalGenerateQuoteId === estimateId,
          );

          if (match) {
            setSelectedEstimate(match);
          } else {
            swal("Error", "Estimate not found.", "error").then(() =>
              history("/order/management?tab=estimate"),
            );
          }
        } else {
          setSelectedEstimate(null);
        }
      } catch (err) {
        console.error("fetchEstimate error:", err);
      } finally {
        setPageLoading(false);
        loader(false);
      }
    },
    [history],
  );

  useEffect(() => {
    if (id) fetchEstimate(id);
  }, [id, fetchEstimate, triggerDate]);

  const handleAcceptReject = (item, statusStr) => {
    const isAccepting = statusStr === "estimated" || statusStr === "accepted";
    const statusApiStr = statusStr === "estimated" ? "estimated" : "reject";

    swal({
      title: isAccepting ? "Accept Quote" : "Reject Quote",
      text: `Are you sure you want to ${isAccepting ? "accept" : "reject"} this quote?`,
      icon: "warning",
      buttons: {
        cancel: {
          text: "No",
          visible: true,
          value: false,
          className: "swal-button--cancel",
        },
        confirm: {
          text: "Yes",
          value: true,
          className: isAccepting
            ? "swal-button--confirm"
            : "swal-button--danger",
        },
      },
      dangerMode: !isAccepting,
    }).then((willConfirm) => {
      if (willConfirm) {
        loader(true);
        ApiClient.put("generate-quote/update", {
          status: statusApiStr,
          id: item?._id,
        }).then((res) => {
          if (res.success) {
            swal({
              icon: "success",
              title: isAccepting ? "Estimate Accepted!" : "Estimate Rejected!",
              text: isAccepting
                ? "The estimate has been accepted successfully and moved to the Pending tab. Click below to view the details."
                : "The estimate has been rejected successfully and moved to the Rejected tab. Click below to view the details.",
              buttons: {
                close: {
                  text: "Go Back",
                  value: "close",
                },
                view: {
                  text: "View",
                  value: "view",
                },
              },
            }).then((value) => {
              if (value === "view") {
                history(
                  `/estimate/detail/${res?.data?._id}?date=${Date.now()}`,
                );
              }

              if (value === "close") {
                history(`/order/management?tab=estimate`);
              }
            });
          } else {
            swal(
              "Error",
              res.error?.message || "Something went wrong.",
              "error",
            );
          }
          loader(false);
        });
      }
    });
  };

  return (
    <Layout>
      <div className="max-xl:container xl:max-w-[1280px]  mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={() => history("/order/management?tab=estimate")}
            className="size-9 rounded-xl border border-gray-200 bg-white hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <div>
            <h2 className="text-gray-900 text-xl font-semibold capitalize">
              Estimate Details
            </h2>
            <p className="text-gray-500 text-sm">
              View full details of this estimate
            </p>
          </div>
        </div>

        <div className="w-full border rounded-2xl bg-white flex flex-col">
          {pageLoading ? (
            <div className="flex items-center justify-center py-20">
              {/* <div className="size-8 border-2 border-[#115B9D] border-t-transparent rounded-full animate-spin" /> */}
            </div>
          ) : !selectedEstimate ? (
            <div className="flex items-center justify-center py-20">
              <p className="text-gray-500 text-sm capitalize">
                Estimate not found.
              </p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="min-w-9 w-9 h-9 rounded-xl bg-[#115B9D17] flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="17"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-[#115B9D]"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                    </svg>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={selectedEstimate.status} />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-5 space-y-5">
                {/* Customer Card */}
                <div className="flex items-center gap-4 p-4 bg-[#115B9D08] border border-[#115B9D]/10 rounded-xl">
                  <div className="size-12 rounded-xl bg-[#115B9D17] border border-[#115B9D]/15 flex items-center justify-center shrink-0">
                    <span className="text-sm font-bold text-[#115B9D]">
                      {getInitials(selectedEstimate.customerName)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-semibold capitalize text-gray-900">
                      {selectedEstimate.customerName}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {selectedEstimate.email}
                    </p>
                  </div>
                </div>

                {/* Estimate Details */}
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-gray-600 capitalize tracking-widest">
                    Estimate Details
                  </p>
                  <div className="pt-2 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                      {
                        label: "DOC NUMBER",
                        value: `#${selectedEstimate.docNumber}`,
                      },
                      {
                        label: "TXN DATE",
                        value: formatDate(selectedEstimate.txnDate),
                      },
                      {
                        label: "EXPIRY DATE",
                        value: formatDate(selectedEstimate.expirationDate),
                      },
                      {
                        label: "CREATED ON",
                        value: formatDate(selectedEstimate.createdAt),
                      },
                      {
                        label: "LAST UPDATED",
                        value: formatDate(selectedEstimate.updatedAt),
                      },
                      ...(selectedEstimate.shipMethod
                        ? [
                            {
                              label: "SHIP METHOD",
                              value: selectedEstimate.shipMethod,
                            },
                          ]
                        : []),
                    ].map((d) => (
                      <div
                        key={d.label}
                        className="p-3 border border-gray-100 rounded-xl bg-gray-50/50"
                      >
                        <p className="text-[11px] text-gray-600 font-medium capitalize tracking-wide">
                          {d.label}
                        </p>
                        <p className="text-sm text-gray-800 mt-0.5 break-words">
                          {d.value}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Addresses */}
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-gray-600 capitalize tracking-widest">
                    Addresses
                  </p>
                  <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      {
                        label: "BILLING ADDRESS",
                        value: formatBillAddr(selectedEstimate.BillAddr),
                      },
                      {
                        label: "SHIPPING ADDRESS",
                        value: formatShipAddr(selectedEstimate.ShipAddr),
                      },
                    ].map(({ label, value }) => (
                      <div
                        key={label}
                        className="flex items-start gap-3 p-3 border border-gray-100 rounded-xl bg-gray-50/50"
                      >
                        <div className="size-7 rounded-lg bg-[#115B9D17] flex items-center justify-center shrink-0 mt-0.5">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-[#115B9D]"
                          >
                            <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" />
                            <circle cx="12" cy="10" r="3" />
                          </svg>
                        </div>
                        <div className="min-w-0">
                          <p className="text-[11px] text-gray-600 font-medium capitalize tracking-wide">
                            {label}
                          </p>
                          <p className="text-sm text-gray-800 mt-0.5 break-words">
                            {value}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Line Items */}
                {selectedEstimate.Line?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-gray-600 capitalize tracking-widest">
                      Line Items
                    </p>
                    <div className="border border-gray-100 rounded-xl overflow-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-100">
                          <tr>
                            {["Item", "Qty", "Unit Price", "Amount"].map(
                              (h, i) => (
                                <th
                                  key={h}
                                  className={`py-2.5 px-4 text-xs font-medium text-gray-500 whitespace-nowrap capitalize ${
                                    i === 0
                                      ? "text-left"
                                      : i === 1
                                        ? "text-center"
                                        : "text-right"
                                  }`}
                                >
                                  {h}
                                </th>
                              ),
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {selectedEstimate.Line.map((line, i) => (
                            <tr
                              key={line.id || i}
                              className="hover:bg-gray-50/50"
                            >
                              <td className="py-2.5 px-4">
                                <p className="text-sm text-gray-700 min-w-[150px]">
                                  {line.name}
                                </p>
                              </td>
                              <td className="py-2.5 px-4 text-center">
                                <span className="text-sm text-gray-600">
                                  {line.qty}
                                </span>
                              </td>
                              <td className="py-2.5 px-4 text-right">
                                <span className="text-sm text-gray-600">
                                  {formatCurrency(
                                    line.unitPrice,
                                    selectedEstimate.currencyCode,
                                  )}
                                </span>
                              </td>
                              <td className="py-2.5 px-4 text-right">
                                <span className="text-sm font-medium text-gray-800">
                                  {formatCurrency(
                                    line.amount,
                                    selectedEstimate.currencyCode,
                                  )}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Tax Detail */}
                {selectedEstimate.totalTax > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-gray-600 capitalize tracking-widest">
                      Tax Detail
                    </p>
                    <div className="border border-gray-100 rounded-xl overflow-hidden">
                      {selectedEstimate.taxLines?.length > 0 ? (
                        selectedEstimate.taxLines.map((tl, i) => (
                          <div
                            key={i}
                            className="flex gap-x-3 items-center justify-between px-4 py-3 border-b border-gray-50 last:border-0"
                          >
                            <p className="text-sm text-gray-500 capitalize">
                              Tax ({tl.percent}%) on{" "}
                              {formatCurrency(
                                tl.netAmount,
                                selectedEstimate.currencyCode,
                              )}
                            </p>
                            <p className="text-sm font-semibold text-gray-800 capitalize">
                              {formatCurrency(
                                tl.amount,
                                selectedEstimate.currencyCode,
                              )}
                            </p>
                          </div>
                        ))
                      ) : (
                        <div className="flex gap-x-3 items-center justify-between px-4 py-3 border-b border-gray-50 last:border-0">
                          <p className="text-sm text-gray-500 capitalize">
                            Total Tax
                          </p>
                          <p className="text-sm font-semibold text-gray-800 capitalize">
                            {formatCurrency(
                              selectedEstimate.totalTax,
                              selectedEstimate.currencyCode,
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Summary */}
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-gray-600 capitalize tracking-widest">
                    Summary
                  </p>
                  <div className="border border-gray-100 rounded-xl overflow-hidden">
                    {[
                      {
                        label: "SUBTOTAL",
                        value: formatCurrency(
                          selectedEstimate.subTotal,
                          selectedEstimate.currencyCode,
                        ),
                        highlight: false,
                      },
                      {
                        label:
                          selectedEstimate.markup_type === "percentage"
                            ? `Markup (${selectedEstimate.markup_value}%)`
                            : "Markup",
                        value: formatCurrency(
                          selectedEstimate.markup_amount,
                          selectedEstimate.currencyCode,
                        ),
                        highlight: false,
                      },
                      {
                        label: "TOTAL TAX",
                        value: formatCurrency(
                          selectedEstimate.totalTax,
                          selectedEstimate.currencyCode,
                        ),
                        highlight: false,
                      },
                      {
                        label: selectedEstimate.isDiscountPercent
                          ? `DISCOUNT (${selectedEstimate.discountValue}%)`
                          : "DISCOUNT",
                        value: `- ${formatCurrency(
                          selectedEstimate.discountAmount,
                          selectedEstimate.currencyCode,
                        )}`,
                        highlight: false,
                        hide: !selectedEstimate.discountAmount,
                      },
                      {
                        label: "TOTAL",
                        value: formatCurrency(
                          selectedEstimate.subTotal +
                            selectedEstimate.markup_amount -
                            selectedEstimate.discountAmount +
                            selectedEstimate.totalTax,
                          selectedEstimate.currencyCode,
                        ),
                        highlight: false,
                      },
                      {
                        label: selectedEstimate.partialMode === "percentage"
                          ? `PARTIAL PAYMENT (${selectedEstimate.partialValue}%)`
                          : "PARTIAL PAYMENT",
                        value: formatCurrency(
                          selectedEstimate.partialMode === "percentage"
                            ? (Number(selectedEstimate.partialValue) / 100) *
                              (selectedEstimate.subTotal +
                                selectedEstimate.markup_amount -
                                selectedEstimate.discountAmount +
                                selectedEstimate.totalTax)
                            : Number(selectedEstimate.partialValue),
                          selectedEstimate.currencyCode,
                        ),
                        highlight: true,
                        hide:
                          selectedEstimate.estimateType !== "partial" ||
                          !selectedEstimate.partialValue,
                      },
                    ]
                      .filter((r) => !r.hide)
                      .map((row) => (
                        <div
                          key={row.label}
                          className={`flex items-center justify-between px-4 py-3 border-b border-gray-50 last:border-0 ${
                            row.highlight ? "bg-[#115B9D]/5" : ""
                          }`}
                        >
                          <p
                            className={`text-sm font-medium ${
                              row.highlight ? "text-[#115B9D]" : "text-gray-500"
                            } capitalize`}
                          >
                            {row.label}
                          </p>
                          <p
                            className={`text-sm font-semibold ${
                              row.highlight ? "text-[#115B9D]" : "text-gray-800"
                            } capitalize`}
                          >
                            {row.value}
                          </p>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Negotiation Message */}
                {selectedEstimate.negotiateMessage && (
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-gray-600 capitalize tracking-widest">
                      Negotiation Message
                    </p>
                    <div className="p-3 border border-gray-100 rounded-xl bg-gray-50/50">
                      <p className="text-sm text-gray-800 break-words">
                        {selectedEstimate.negotiateMessage}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 shrink-0">
                {canConvert(selectedEstimate) && (
                  <button
                    type="button"
                    onClick={() =>
                      history(
                        `/generate/invoice/${selectedEstimate?.qbId || selectedEstimate?._id}`,
                      )
                    }
                    className="inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl font-medium px-5 py-2.5 capitalize"
                  >
                    Convert To Invoice
                  </button>
                )}
                {selectedEstimate?.status === "negotiate" && (
                  <>
                    <button
                      type="button"
                      onClick={() =>
                        handleAcceptReject(selectedEstimate, "estimated")
                      }
                      className="inline-flex items-center gap-2 bg-[green] hover:opacity-80 text-white text-sm rounded-xl font-medium px-5 py-2.5 capitalize"
                    >
                      Accept
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleAcceptReject(selectedEstimate, "reject")
                      }
                      className="inline-flex items-center gap-2 bg-red-500 hover:opacity-80 text-white text-sm rounded-xl font-medium px-5 py-2.5 capitalize"
                    >
                      Reject
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        history(
                          `/edit/quote/${selectedEstimate?.qbId || selectedEstimate?._id}`,
                        )
                      }
                      className="inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl font-medium px-5 py-2.5 capitalize"
                    >
                      Edit
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => history("/order/management?tab=estimate")}
                  className="inline-flex items-center gap-2 text-sm text-gray-600 hover:opacity-80 rounded-xl border border-gray-200 bg-white font-medium px-5 py-2.5 capitalize"
                >
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
